import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, ExternalLink, Info } from "lucide-react";
import * as DEFAULTS from "@/site";
import { api, errorText, fmtDate } from "@/admin/api";
import { CONTENT_SECTIONS } from "@/admin/contentSections";
import { FieldEditor } from "@/admin/FieldEditor";
import { PageHeader, Button, Badge, Spinner } from "@/admin/ui";

const clone = (v) => JSON.parse(JSON.stringify(v));

export default function ContentEditor() {
  const [overrides, setOverrides] = useState(null);
  const [active, setActive] = useState("HERO");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setOverrides(Object.fromEntries(r.data.items.map((i) => [i.key, i]))));
  }, []);

  const current = useMemo(() => (overrides ? overrides[active]?.value ?? DEFAULTS[active] : null), [overrides, active]);
  useEffect(() => { if (current !== null && current !== undefined) setDraft(clone(current)); }, [current]);

  const meta = CONTENT_SECTIONS.flatMap((g) => g.items).find((i) => i.key === active);
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(current);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/content/${active}`, { value: draft });
      setOverrides((o) => ({ ...o, [active]: { key: active, value: clone(draft), updated_at: new Date().toISOString() } }));
      toast.success(`${meta.label} published — live on the site now.`);
    } catch (err) { toast.error(errorText(err)); } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset "${meta.label}" to the original built-in text?`)) return;
    try {
      await api.delete(`/content/${active}`);
      setOverrides((o) => { const n = { ...o }; delete n[active]; return n; });
      toast.success("Reset to default");
    } catch (err) { toast.error(errorText(err)); }
  };

  if (!overrides) return <Spinner />;

  return (
    <div data-testid="admin-content">
      <PageHeader
        eyebrow="Website content"
        title="Edit what visitors see"
        sub="Changes publish instantly to desantrix.com. Anything you haven't edited keeps its built-in default."
        actions={<a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:border-amber-500"><ExternalLink className="h-3.5 w-3.5" /> Preview site</a>}
      />
      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        <aside className="lg:sticky lg:top-8 self-start space-y-5" data-testid="content-sections">
          {CONTENT_SECTIONS.map((g) => (
            <div key={g.group}>
              <p className="mb-2 px-2 font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400">{g.group}</p>
              <div className="flex flex-col gap-0.5">
                {g.items.map((i) => (
                  <button
                    key={i.key}
                    onClick={() => { if (dirty && !window.confirm("Discard unsaved changes?")) return; setActive(i.key); }}
                    data-testid={`content-section-${i.key}`}
                    className={`flex items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition-colors ${active === i.key ? "bg-[#0B1528] text-white" : "text-slate-600 hover:bg-white"}`}
                  >
                    {i.label}
                    {overrides[i.key] && <span className={`h-1.5 w-1.5 rounded-full ${active === i.key ? "bg-amber-400" : "bg-amber-500"}`} title="Edited" />}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm min-w-0" data-testid="content-editor-panel">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-[#0B1528]">{meta.label}</h2>
              {meta.hint && <p className="mt-1 flex items-start gap-1.5 text-xs text-slate-500"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />{meta.hint}</p>}
            </div>
            {overrides[active] ? <Badge tone="amber">Edited {fmtDate(overrides[active].updated_at)}</Badge> : <Badge>Default</Badge>}
          </div>

          {draft !== null && <FieldEditor value={draft} sample={DEFAULTS[active]} onChange={setDraft} path={active} />}

          <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-6">
            <Button onClick={save} loading={saving} disabled={!dirty} data-testid="content-save"><Save className="h-4 w-4" /> Publish changes</Button>
            {dirty && <Button variant="ghost" onClick={() => setDraft(clone(current))} data-testid="content-discard">Discard</Button>}
            {overrides[active] && <Button variant="ghost" onClick={reset} className="ml-auto" data-testid="content-reset"><RotateCcw className="h-4 w-4" /> Reset to default</Button>}
          </div>
        </section>
      </div>
    </div>
  );
}
